import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useId, useState, type FormEvent } from "react";

import { queryKey } from "../../../api/endpoints";
import { refusalText } from "../../../api/errors";
import { useApiCommand, useApiQuery } from "../../../api/hooks";
import type { ModelConnectionView, TestResult } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { Button } from "../../../ui/Button";
import { Card } from "../../../ui/Card";
import { ConfirmDialog } from "../../../ui/ConfirmDialog";
import { Loading } from "../../../ui/EmptyState";
import { TextField } from "../../../ui/TextField";
import { showToast } from "../../../ui/toastStore";
import { KIND_CHOICES } from "../profile/PersonalKey";
import { ModelChoice } from "../shared/ModelChoice";
import { FoundList, TestResultView } from "../shared/TestResultView";
import { formatWhen } from "../shared/format";
import { useModelList } from "../shared/useModelList";
import { TEST_RESULT_WORDS } from "../shared/words";
import { contextWarning, leastOverall } from "./contextWarning";

/**
 * The starting values of the form when the server sends none (the settings `model.default_*`). There is no model name: the
 * admin presses Connect and picks one from the list the service gives.
 */
export const DEFAULTS = {
  kind: "anthropic",
  url: "https://api.anthropic.com",
  context_size: 200000,
} as const;

function ConnectionForm({ view }: { view: ModelConnectionView }) {
  const queryClient = useQueryClient();
  const save = useApiCommand("set_model_connection");
  const test = useApiCommand("test_model_connection");
  const remove = useApiCommand("remove_model_connection");
  const askModels = useApiCommand("list_models");
  const models = useModelList();
  const ids = { kind: useId(), size: useId() };
  const [kind, setKind] = useState(view.kind || DEFAULTS.kind);
  const [url, setUrl] = useState(view.url || DEFAULTS.url);
  const [modelName, setModelName] = useState(view.model_name);
  const [contextSize, setContextSize] = useState(String(view.context_size || DEFAULTS.context_size));
  // The key is write-only: this box starts empty whatever the server says, and is emptied after every save.
  const [key, setKey] = useState("");
  const [fresh, setFresh] = useState<TestResult | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const size = Number.parseInt(contextSize, 10);
  const sizeValid = Number.isFinite(size) && size > 0;
  const dirty = kind !== view.kind || url !== view.url || modelName !== view.model_name || (sizeValid && size !== view.context_size) || key.length > 0;
  const warning = sizeValid ? contextWarning(size, view.least_context_per_mode) : null;
  const typedKey = key.trim() !== "";
  // The stored key is used only for the stored kind and address: change either and the key must be typed again.
  const sameAddress = kind === view.kind && url.trim() === view.url;
  const useStored = view.key_set && !typedKey && sameAddress;
  const listed = models.state.phase === "listed" || models.state.phase === "no_list";
  // The model is the stored one only while the address is the stored one; after a change it must come from a fresh list.
  const modelUsable = modelName.trim() !== "" && (listed || (sameAddress && models.state.phase === "idle"));
  const ready = url.trim() !== "" && modelUsable && sizeValid;
  const asking = models.state.phase === "loading";
  const busy = save.isPending || test.isPending || remove.isPending || asking;
  const canAsk = url.trim() !== "" && (useStored || typedKey);
  const canSave = ready && dirty && !busy;

  // The kind, the address or the key changed: what the service said last time no longer applies.
  const changed = (change: () => void) => {
    change();
    models.reset();
    setProblem(null);
  };

  const connect = async () => {
    if (!canAsk || asking) return;
    setProblem(null);
    const answer = await models.ask(() => askModels.mutateAsync({ kind, url: url.trim(), ...(typedKey ? { key: key.trim() } : {}) }));
    // A model that the new list does not offer is dropped; with no list, the name typed before stays only for the same address.
    if (answer?.phase === "listed") setModelName((now) => (answer.models.includes(now) ? now : ""));
    else if (answer?.phase === "no_list") setModelName((now) => (sameAddress ? now : ""));
  };

  const keep = (next: ModelConnectionView) => queryClient.setQueryData(queryKey("model_connection", {}) as unknown as QueryKey, next);

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!ready) return;
    setProblem(null);
    try {
      const reply = await save.mutateAsync({
        kind,
        url: url.trim(),
        model_name: modelName.trim(),
        context_size: size,
        ...(key.trim() ? { key: key.trim() } : {}),
      });
      setKey("");
      setFresh(null);
      keep(reply.data);
      showToast({ text: "The model connection is saved." });
    } catch (error) {
      setKey("");
      models.reset();
      setProblem(refusalText(error));
    }
  };

  const runTest = async () => {
    setProblem(null);
    setFresh(null);
    try {
      const reply = await test.mutateAsync({});
      setFresh(reply.data);
      void queryClient.invalidateQueries({ queryKey: ["model_connection"] });
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  const doRemove = async () => {
    setProblem(null);
    try {
      const reply = await remove.mutateAsync({});
      keep(reply.data);
      setConfirmRemove(false);
      setFresh(null);
      showToast({ text: "The model connection is removed. The agent is unavailable until a new one is set." });
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  const known = view.last_test_result ? (TEST_RESULT_WORDS as Record<string, { headline: string } | undefined>)[view.last_test_result] : undefined;
  const lastWords = view.last_test_result ? (known?.headline ?? view.last_test_result) : null;
  const most = leastOverall(view.least_context_per_mode);

  return (
    <form className="stack" onSubmit={(event) => void submit(event)} autoComplete="off">
      <div className="field">
        <label htmlFor={ids.kind} className="field__label">
          Kind of service
        </label>
        <select id={ids.kind} className="field__input" value={kind} onChange={(event) => changed(() => setKind(event.target.value))}>
          {KIND_CHOICES.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </select>
      </div>
      <TextField
        label="Address"
        hint="Must start with https. Plain http is only allowed for a server on a private network."
        value={url}
        onChange={(event) => changed(() => setUrl(event.target.value))}
        inputMode="url"
        autoComplete="off"
      />
      <TextField
        label="Key"
        hint={view.key_set ? "A key is set. Type a new one to replace it. It is never shown again." : "Paste the key here. It is write-only: it is never shown again."}
        type="password"
        value={key}
        onChange={(event) => changed(() => setKey(event.target.value))}
        // Enter in the key box asks for the models; when Save is ready it saves, as a form does.
        onKeyDown={(event) => {
          if (event.key !== "Enter" || canSave || !canAsk) return;
          event.preventDefault();
          void connect();
        }}
        placeholder={view.key_set ? "A key is set" : ""}
        autoComplete="off"
        spellCheck={false}
      />
      <div className="stack">
        <div className="row">
          {/* Not disabled while the service is asked: the keyboard focus stays on the button, and a second press is ignored. */}
          <Button
            variant={modelUsable ? "secondary" : "primary"}
            onClick={() => void connect()}
            aria-busy={asking || undefined}
            disabled={!canAsk || save.isPending || test.isPending || remove.isPending}
            aria-describedby="ra-connect-hint"
          >
            {useStored ? "Change model" : "Connect"}
          </Button>
        </div>
        <p id="ra-connect-hint" className="muted">
          {useStored
            ? "Change model asks the service for its list of models, with the key that is stored."
            : view.key_set && !typedKey
              ? "The kind of service or the address was changed, so type the key again, then press Connect."
              : "Paste the key, then press Connect. The service tells which models it offers, and you pick one."}
        </p>
        {models.state.phase === "idle" ? (
          modelUsable ? (
            <div className="field">
              <span className="field__label">Model</span>
              <p className="ra-model-current">{modelName}</p>
            </div>
          ) : (
            <p className="muted">No model is chosen yet. Press Connect to see the models this service offers.</p>
          )
        ) : (
          <ModelChoice
            state={models.state}
            value={modelName}
            onChange={setModelName}
            hint="These are the models the service offers. The agent uses the one you pick."
          />
        )}
      </div>
      <TextField
        label="Context size"
        hint={`How much text the model can read at once, in tokens.${most > 0 ? ` The biggest step needs at least ${most.toLocaleString("en-US")}.` : ""}`}
        type="number"
        min={1}
        value={contextSize}
        onChange={(event) => setContextSize(event.target.value)}
        id={ids.size}
        error={!sizeValid ? "Type a whole number above zero." : null}
      />
      {(warning || view.warning) && (
        <p role="note" className="ra-warning">
          {warning ?? view.warning}
        </p>
      )}
      <div className="row">
        <Button type="submit" variant="primary" busy={save.isPending} disabled={!canSave}>
          Save the connection
        </Button>
        <Button onClick={() => void runTest()} busy={test.isPending} disabled={dirty || busy} aria-describedby="ra-test-hint">
          Test connection
        </Button>
        {view.key_set && (
          <Button variant="quiet" onClick={() => setConfirmRemove(true)} disabled={busy}>
            Remove the connection
          </Button>
        )}
      </div>
      <p id="ra-test-hint" className="muted">
        {dirty ? "Save your changes first, then test." : "Test connection asks the model a small question, then tries a tool call, a streamed answer and a small picture."}
      </p>
      {problem && (
        <p role="alert" className="form-error">
          {problem}
        </p>
      )}

      {fresh ? (
        <TestResultView result={fresh} />
      ) : (
        view.last_test_at &&
        lastWords && (
          <div className="ra-test" role="status">
            <p>
              Last test, {formatWhen(view.last_test_at)}: <strong>{lastWords}</strong>
            </p>
            <FoundList found={view.found} />
          </div>
        )
      )}

      <ConfirmDialog
        open={confirmRemove}
        title="Remove the model connection?"
        message="The agent becomes unavailable for everyone who has no key of their own. Adding by hand, reviewing and every other step that needs no agent keep working."
        confirmLabel="Remove the connection"
        cancelLabel="Keep it"
        busy={remove.isPending}
        error={problem}
        onConfirm={() => void doRemove()}
        onCancel={() => setConfirmRemove(false)}
      />
    </form>
  );
}

/**
 * The model connection (ui-direction screen 10): kind, address and a write-only key; Connect (or Change model, with the stored
 * key) asks the service which models it offers, and the admin picks one from a drop-down; then the context size, Save and Test
 * connection. No model name is preset. A service with no list lets the admin type the name.
 */
export function ModelConnectionSection({ pointed }: { pointed: boolean }) {
  const query = useApiQuery("model_connection", {});
  const view = query.data;
  // In demo mode the agent does answer, from recorded examples: say so, as the banner on the same page does (UI-21).
  const demo = useCurrentMe().agent.demo;
  return (
    <Card className="ra-section" id="model-connection" aria-labelledby="ra-model-title" data-pointed={pointed ? "true" : undefined}>
      <h2 id="ra-model-title">Model connection</h2>
      <p className="muted">
        The agent needs a model. The key is encrypted on the server and never shown again; this screen only says whether one is set. People may add a key of their own on their profile.
      </p>
      {query.isPending && <Loading>Reading the model connection</Loading>}
      {query.isError && (
        <p role="alert" className="form-error">
          {refusalText(query.error)} <Button onClick={() => void query.refetch()}>Try again</Button>
        </p>
      )}
      {view && (
        <>
          <p role="status">
            <strong>{view.key_set ? "A key is set." : "No key is set."}</strong> {view.key_set ? "" : demo ? "The agent is answering from recorded examples until one is." : "The agent is unavailable until one is."}
          </p>
          <ConnectionForm key={`${view.kind}|${view.url}|${view.model_name}|${view.context_size}|${view.key_set}`} view={view} />
        </>
      )}
    </Card>
  );
}
